import { memo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { UfSelect } from "@/shared/components/UfSelect";
import {
  formatCEP,
  formatDocument,
  formatPhone,
} from "@/shared/lib/formatters";
import { PersonType } from "@/shared/types/enums";
import type { NewClientData } from "../../utils/rental-create-payload";
import {
  CNPJ_LENGTH,
  CPF_LENGTH,
  cleanDocument,
  normalizeDocumentInput,
} from "@/shared/lib/document";

const schema = z
  .object({
    personType: z.nativeEnum(PersonType),
    name: z.string().min(1, "Nome obrigatório").max(150),
    document: z.string().min(11, "Documento obrigatório"),
    gender: z.enum(["M", "F"]).or(z.literal("")).optional(),
    phone: z.string().min(10, "Telefone obrigatório"),
    email: z.string().email("E-mail inválido").optional().or(z.literal("")),
    postalCode: z.string().min(8, "CEP obrigatório"),
    street: z.string().min(1, "Rua obrigatória"),
    number: z
      .string()
      .min(1, "Número obrigatório")
      .max(10, "Número deve ter no máximo 10 caracteres"),
    complement: z.string(),
    neighborhood: z.string().min(1, "Bairro obrigatório"),
    state: z.string().min(2, "UF obrigatória").max(2, "UF deve ter 2 letras"),
  })
  .superRefine((data, ctx) => {
    const document = cleanDocument(data.document);
    const isJuridica = data.personType === PersonType.JURIDICA;
    if (
      isJuridica
        ? document.length !== CNPJ_LENGTH
        : document.length !== CPF_LENGTH
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["document"],
        message: isJuridica ? "CNPJ inválido" : "CPF inválido",
      });
    }
    if (!isJuridica && !data.gender) {
      ctx.addIssue({
        code: "custom",
        path: ["gender"],
        message: "Gênero obrigatório",
      });
    }
  });

type FormData = z.infer<typeof schema>;

interface ClientCreateDialogProps {
  open: boolean;
  defaultValues?: NewClientData | null;
  onClose: () => void;
  onSubmit: (data: NewClientData) => void;
}

/**
 * Modal de cadastro de cliente sem chamada de API.
 *
 * Os dados são devolvidos ao formulário pai via `onSubmit` e só
 * persistem no servidor quando o aluguel é criado (`POST /rentals`
 * aceita criar cliente no mesmo payload). Inputs de CPF/CEP/celular
 * exibem formatação mas o estado guarda apenas dígitos.
 *
 * @example
 * <ClientCreateDialog
 *   open={dialogOpen}
 *   defaultValues={form.getValues('newClient')}
 *   onClose={() => setDialogOpen(false)}
 *   onSubmit={handleClientCreate}
 * />
 */
export const ClientCreateDialog = memo(function ClientCreateDialog({
  open,
  defaultValues,
  onClose,
  onSubmit,
}: ClientCreateDialogProps) {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      personType: defaultValues?.personType ?? PersonType.FISICA,
      name: defaultValues?.name ?? "",
      document: defaultValues?.document ?? "",
      gender: defaultValues?.gender ?? "M",
      phone: defaultValues?.phone ?? "",
      email: defaultValues?.email ?? "",
      postalCode: defaultValues?.postalCode ?? "",
      street: defaultValues?.street ?? "",
      number: defaultValues?.number ?? "",
      complement: defaultValues?.complement ?? "",
      neighborhood: defaultValues?.neighborhood ?? "",
      state: defaultValues?.state ?? "DF",
    },
  });

  const isJuridica = form.watch("personType") === PersonType.JURIDICA;

  const handleSubmit = (data: FormData) => {
    const juridica = data.personType === PersonType.JURIDICA;
    onSubmit({
      name: data.name,
      document: data.document,
      personType: data.personType,
      gender: juridica ? "" : (data.gender as "M" | "F"),
      phone: data.phone,
      email: data.email || undefined,
      street: data.street,
      number: data.number,
      complement: data.complement,
      neighborhood: data.neighborhood,
      state: data.state,
      postalCode: data.postalCode,
    });
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cadastrar cliente</DialogTitle>
          <DialogDescription>
            Os dados serão enviados junto com o aluguel — o cliente é criado no
            servidor apenas quando o aluguel for finalizado.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="personType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de pessoa *</FormLabel>
                  <FormControl>
                    <select
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value as PersonType;
                        field.onChange(value);
                        if (value === PersonType.JURIDICA) {
                          form.setValue("gender", "");
                        } else if (!form.getValues("gender")) {
                          form.setValue("gender", "M");
                        }
                      }}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm md:max-w-xs"
                    >
                      <option value={PersonType.FISICA}>
                        Pessoa Física (CPF)
                      </option>
                      <option value={PersonType.JURIDICA}>
                        Pessoa Jurídica (CNPJ)
                      </option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isJuridica ? "Razão Social *" : "Nome *"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          isJuridica ? "Manicorp Trajes LTDA" : "João da Silva"
                        }
                        maxLength={150}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="document"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isJuridica ? "CNPJ *" : "CPF *"}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          isJuridica ? "11.222.333/0001-81" : "000.000.000-00"
                        }
                        value={field.value ? formatDocument(field.value) : ""}
                        onChange={(e) => {
                          field.onChange(
                            normalizeDocumentInput(e.target.value, isJuridica),
                          );
                        }}
                        maxLength={18}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {!isJuridica && (
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gênero *</FormLabel>
                      <FormControl>
                        <select
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                        >
                          <option value="M">Masculino</option>
                          <option value="F">Feminino</option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="(11) 99999-9999"
                        value={field.value ? formatPhone(field.value) : ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, "");
                          field.onChange(raw);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="email@exemplo.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CEP *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="00000-000"
                        value={field.value ? formatCEP(field.value) : ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, "");
                          field.onChange(raw);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UF *</FormLabel>
                    <FormControl>
                      <UfSelect value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rua *</FormLabel>
                    <FormControl>
                      <Input placeholder="Av. Paulista" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número *</FormLabel>
                    <FormControl>
                      <Input placeholder="123" maxLength={10} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="complement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Complemento</FormLabel>
                    <FormControl>
                      <Input placeholder="Apto 101" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="neighborhood"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bairro *</FormLabel>
                    <FormControl>
                      <Input placeholder="Bela Vista" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit">Salvar cliente</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
